import { useMemo } from 'react';
import type { Fund, Holder, RegistryState, UnitClass } from '../data/types';
import { closedHoldings, type Holding } from '../domain/holdings';
import { useRegistry } from './RegistryStore';

export interface TenantScope {
  tenant: RegistryState['tenants'][number];
  user: RegistryState['users'][number];
  funds: Fund[];
  classes: UnitClass[];
  holders: Holder[];
  holdings: Holding[];
  fundIds: Set<string>;
  holderIds: Set<string>;
  fundById: Map<string, Fund>;
  classById: Map<string, UnitClass>;
  holderById: Map<string, Holder>;
  /** The registry record for each platform investment entity. */
  holderByEntityId: Map<string, Holder>;
  calls: RegistryState['calls'];
  distributions: RegistryState['distributions'];
  applications: RegistryState['applications'];
  approvals: RegistryState['approvals'];
  kycCases: RegistryState['kycCases'];
  bankLines: RegistryState['bankLines'];
  changeRequests: RegistryState['changeRequests'];
  taxBatches: RegistryState['taxBatches'];
  ledger: RegistryState['ledger'];
  migrations: RegistryState['migrations'];
  documents: RegistryState['documents'];
  audit: RegistryState['audit'];
  capitalReturns: RegistryState['capitalReturns'];
  redemptionRuns: RegistryState['redemptionRuns'];
  paymentExceptions: RegistryState['paymentExceptions'];
  communications: RegistryState['communications'];
  opportunities: RegistryState['opportunities'];
  fundSetups: RegistryState['fundSetups'];
  offers: RegistryState['offers'];
  issueRuns: RegistryState['issueRuns'];
  reportRuns: RegistryState['reportRuns'];
}

export function buildTenantScope(state: RegistryState, holdings: Holding[]): TenantScope {
  const tenantId = state.currentTenantId;
  const funds = state.funds.filter((fund) => fund.tenantId === tenantId);
  const fundIds = new Set(funds.map((fund) => fund.id));
  const holders = state.holders.filter((holder) => holder.tenantId === tenantId);
  const holderIds = new Set(holders.map((holder) => holder.id));
  const classes = state.classes.filter((unitClass) => fundIds.has(unitClass.fundId));
  const inFund = <T extends { fundId?: string }>(items: T[]) =>
    items.filter((item) => item.fundId && fundIds.has(item.fundId));
  const tenantNames = new Set(
    state.users.filter((user) => user.tenantId === tenantId).map((user) => user.name),
  );
  return {
    tenant: state.tenants.find((tenant) => tenant.id === tenantId)!,
    user: state.users.find((user) => user.id === state.currentUserId)!,
    funds,
    classes,
    holders,
    holdings: holdings.filter((holding) => fundIds.has(holding.fundId)),
    fundIds,
    holderIds,
    fundById: new Map(funds.map((fund) => [fund.id, fund])),
    classById: new Map(classes.map((unitClass) => [unitClass.id, unitClass])),
    holderById: new Map(holders.map((holder) => [holder.id, holder])),
    holderByEntityId: new Map(holders.map((holder) => [holder.investmentEntityId, holder])),
    calls: inFund(state.calls),
    distributions: inFund(state.distributions),
    applications: inFund(state.applications),
    approvals: state.approvals.filter(
      (item) =>
        (item.fundId && fundIds.has(item.fundId)) || (!item.fundId && tenantId === 't-fides'),
    ),
    kycCases: state.kycCases.filter(
      (item) =>
        (item.holderId && holderIds.has(item.holderId)) ||
        (item.applicationId &&
          state.applications.some(
            (app) => app.id === item.applicationId && fundIds.has(app.fundId),
          )),
    ),
    bankLines: inFund(state.bankLines),
    changeRequests: state.changeRequests.filter((item) => holderIds.has(item.holderId)),
    taxBatches: inFund(state.taxBatches),
    ledger: inFund(state.ledger),
    migrations: inFund(state.migrations),
    documents: state.documents.filter(
      (doc) =>
        (doc.holderId && holderIds.has(doc.holderId)) || (doc.fundId && fundIds.has(doc.fundId)),
    ),
    capitalReturns: inFund(state.capitalReturns),
    redemptionRuns: inFund(state.redemptionRuns),
    paymentExceptions: inFund(state.paymentExceptions),
    communications: state.communications.filter(
      (log) =>
        (log.fundId && fundIds.has(log.fundId)) ||
        (!log.fundId && log.holderIds.some((id) => holderIds.has(id))),
    ),
    opportunities: state.opportunities.filter((item) => item.tenantId === tenantId),
    fundSetups: state.fundSetups.filter((item) => item.tenantId === tenantId),
    offers: state.offers.filter((item) => item.tenantId === tenantId),
    issueRuns: state.issueRuns.filter((item) => item.tenantId === tenantId),
    reportRuns: state.reportRuns.filter((item) => item.tenantId === tenantId),
    audit: state.audit.filter((event) =>
      tenantId === 't-fides'
        ? !state.users.some((user) => user.tenantId !== tenantId && user.name === event.actor)
        : tenantNames.has(event.actor),
    ),
  };
}

export function useTenantScope(): TenantScope {
  const { state, holdings } = useRegistry();
  return useMemo(() => buildTenantScope(state, holdings), [state, holdings]);
}

export function useInvestorScope() {
  const { state, holdings } = useRegistry();
  return useMemo(() => {
    const login = state.investorLogins.find((item) => item.id === state.currentInvestorLoginId)!;
    const holderIds = new Set(login.holderIds);
    const holders = state.holders.filter((holder) => holderIds.has(holder.id));
    return {
      login,
      tenant: state.tenants.find((tenant) => tenant.id === login.tenantId)!,
      holders,
      holderIds,
      holdings: holdings.filter((holding) => holderIds.has(holding.holderId)),
      ledger: state.ledger.filter((entry) => holderIds.has(entry.holderId)),
      tasks: state.tasks.filter((task) => holderIds.has(task.holderId)),
      documents: state.documents.filter(
        (doc) => doc.visibleToInvestor && doc.holderId && holderIds.has(doc.holderId),
      ),
      calls: state.calls.filter(
        (call) =>
          call.status !== 'DRAFT' &&
          call.status !== 'PENDING_APPROVAL' &&
          call.lines.some((line) => holderIds.has(line.holderId)),
      ),
      distributions: state.distributions.filter(
        (distribution) =>
          distribution.status === 'PAID' &&
          distribution.lines.some((line) => holderIds.has(line.holderId)),
      ),
      applications: state.applications.filter(
        (application) =>
          application.investorLoginId === login.id ||
          holders.some((holder) => holder.investmentEntityId === application.investmentEntityId),
      ),
      changeRequests: state.changeRequests.filter((request) => holderIds.has(request.holderId)),
      funds: state.funds.filter((fund) => fund.tenantId === login.tenantId),
      closedHoldings: closedHoldings(
        state.ledger.filter((entry) => holderIds.has(entry.holderId)),
        holdings.filter((holding) => holderIds.has(holding.holderId)),
      ),
      capitalReturns: state.capitalReturns.filter(
        (item) => item.status === 'PAID' && item.lines.some((line) => holderIds.has(line.holderId)),
      ),
      communications: state.communications.filter((log) =>
        log.holderIds.some((id) => holderIds.has(id)),
      ),
      paymentExceptions: state.paymentExceptions.filter(
        (item) =>
          holderIds.has(item.holderId) && item.status !== 'REISSUED' && item.status !== 'UNCLAIMED',
      ),
      redemptionRuns: state.redemptionRuns,
      classes: state.classes,
      offers: state.offers.filter(
        (offer) => offer.tenantId === login.tenantId && offer.status === 'PUBLISHED',
      ),
    };
  }, [state, holdings]);
}

export function sumBy<T>(items: T[], pick: (item: T) => number): number {
  return items.reduce((total, item) => total + pick(item), 0);
}
